// ═══════════════════════════════════════════════════════════════
// PwaInstallNudge.jsx — PWA installation transition nudge
// Detects when user installs the PWA and nudges them to open it
// from the home screen. Also shows a banner if they return to
// the browser after installing.
// Purely additive — no effect on existing logic.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { X, Smartphone, CheckCircle } from 'lucide-react';
import ModalDialog from './ModalDialog.jsx';

const PWA_INSTALLED_KEY = 'argumentor-pwa-installed';
const NUDGE_DISMISSED_KEY = 'argumentor-pwa-nudge-dismissed';

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true;
}

/**
 * Post-install celebration modal.
 * Shown once right after the user installs the PWA.
 */
function InstallSuccessModal({ onClose }) {
  return (
    <ModalDialog onClose={onClose} labelledBy="pwa-install-success-title" className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden text-center">
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6">
          <img src="/argumentor_peace.jpg" alt="TAM" className="w-20 h-20 mx-auto mb-3 rounded-full bg-white/20 p-1" />
          <h2 id="pwa-install-success-title" className="text-xl font-bold text-white">You're all set!</h2>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-center gap-2 mb-3">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="text-sm font-medium text-green-700">App installed successfully</span>
          </div>
          <p className="text-sm text-stone-600 mb-1">
            TAM is ready on your home screen!
          </p>
          <p className="text-xs text-stone-500 mb-5">
            Tap the app icon to get the full experience: faster loads, push notifications, and offline access.
          </p>
          <button
            onClick={onClose}
            className="w-full py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium transition-colors"
          >
            Got it!
          </button>
        </div>
      </div>
    </ModalDialog>
  );
}

/**
 * Subtle banner shown when user returns to browser after installing PWA.
 */
function BrowserBanner({ onDismiss }) {
  return (
    <div className="bg-purple-600 text-white px-4 py-2.5 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 min-w-0">
        <Smartphone className="w-4 h-4 flex-shrink-0" />
        <p className="text-sm truncate">
          You have the app installed. Open it from your home screen for the best experience!
        </p>
      </div>
      <button
        onClick={onDismiss}
        className="text-white/70 hover:text-white flex-shrink-0 p-1"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

/**
 * PwaInstallNudge — render anywhere in the tree.
 * Listens for appinstalled, manages localStorage flags.
 */
function PwaInstallNudge() {
  const [showModal, setShowModal] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // If already running as PWA, nothing to do
    if (isStandalone()) return;

    // Listen for fresh install
    const handleInstalled = () => {
      localStorage.setItem(PWA_INSTALLED_KEY, 'true');
      localStorage.removeItem(NUDGE_DISMISSED_KEY);
      setShowModal(true);
    };
    window.addEventListener('appinstalled', handleInstalled);

    // Check if PWA was previously installed but user is in browser
    const wasInstalled = localStorage.getItem(PWA_INSTALLED_KEY) === 'true';
    const wasDismissed = localStorage.getItem(NUDGE_DISMISSED_KEY) === 'true';
    if (wasInstalled && !wasDismissed) {
      setShowBanner(true);
    }

    return () => window.removeEventListener('appinstalled', handleInstalled);
  }, []);

  const handleCloseModal = () => {
    setShowModal(false);
    setShowBanner(true);
  };

  const handleDismissBanner = () => {
    setShowBanner(false);
    localStorage.setItem(NUDGE_DISMISSED_KEY, 'true');
  };

  // Nothing to render if in standalone mode
  if (isStandalone()) return null;

  return (
    <>
      {showModal && <InstallSuccessModal onClose={handleCloseModal} />}
      {showBanner && !showModal && <BrowserBanner onDismiss={handleDismissBanner} />}
    </>
  );
}

export default PwaInstallNudge;
